import { action, makeAutoObservable } from 'mobx';
import { defaultThemeSettings } from '@web-chat-core/ui-kit';
import type { BackgroundAnimationsType } from '@web-chat-core/ui-kit/core/BackgroundAnimations/type';
import { makePersistable } from 'mobx-persist-store';
import { SegmentProperty } from 'api';
import type { WebinarModeData } from 'api/types';
import type { ModeratorInfo } from 'api/generated/models/ModeratorInfo';
import { APP_STORE_STORAGE_KEY } from '../../../consts';

interface AppStoreInterface {
    windowInitialData: WindowData | null;
}

export type Theme = {
    mode: 'dark' | 'light';
    variant: 'themeFirst' | 'themeSecond' | 'themeThird';
};

export type WindowData = {
    descriptor: string;
    lang: string;
    theme: Theme;
};

export type CallConfig = {
    isP2pCall: boolean;
    isGroupCall: boolean;
    groupId: number;
    roomName: string;
    initiatorId: number;
    workspaceId: number;
    callId: number;
    isMeInitiatedP2pCall?: boolean;
    isMeInitiatedGroupCall?: boolean;
    auth?: {
        jwtToken: string;
        url: string;
    };
    isDontShowMeetPreview?: boolean;
    isVKSAudioEnabled?: boolean;
    isVKSVideoEnabled?: boolean;
};

export type IncomingCallData = {
    initialData: {
        callerInfo?: IncomingCallUserData;
        currentUserInfo: IncomingCallUserData;
        features?: SegmentProperty[];
    };
    multiServerResources?: {
        apiDomain: string;
    };
    config: CallConfig;
    oAuthToken?: string;
    waitingListId?: string;
    theme?: Theme;
    backgroundAnimation?: BackgroundAnimationsType;
    accountSettings?: {
        vcsAccessRequestsSoundEnabled?: boolean;
        vcsAccessRequestsNotificationsEnabled?: boolean;
        joinApprovalPolicy?: 'REQUIRED_FOR_GUESTS' | 'REQUIRED_FOR_EVERYONE' | 'WITHOUT_RESTRICTIONS';
        onlyModeratorsCanApproveWaitingUsers?: boolean;
        requiresWaitingRoomRejoin?: boolean;
    };
    webinarMode?: WebinarModeData;
    moderators?: ModeratorInfo[];
};

export type IncomingCallUserData = {
    color: string;
    avatarType: string;
    avatarImage: string;
    name: string;
    initials: string;
    id: number;
};

class AppStore implements AppStoreInterface {
    public windowInitialData: WindowData | null = null;
    public callInitialData: IncomingCallData | null = null;
    public theme: Theme = defaultThemeSettings;

    public constructor() {
        makeAutoObservable(this);

        makePersistable(this, {
            name: APP_STORE_STORAGE_KEY,
            properties: ['theme'],
            storage: window.localStorage,
        }).then(() => {
            this.setTheme(this.theme || defaultThemeSettings);
        });
    }

    @action
    public setTheme(theme: Theme) {
        this.theme = theme;
    }

    @action
    public setInitialWindowData = (data: WindowData) => {
        this.windowInitialData = data;
    };

    @action
    public setCallData = (data: IncomingCallData) => {
        this.callInitialData = data;
    };

    @action
    public setIsDontShowMeetPreview = (value: boolean) => {
        if (this.callInitialData) {
            this.callInitialData = {
                ...this.callInitialData,
                config: { ...this.callInitialData.config, isDontShowMeetPreview: value },
            };
        }
    };

    @action
    public setIsVKSAudioEnabled = (value: boolean) => {
        if (this.callInitialData) {
            this.callInitialData = {
                ...this.callInitialData,
                config: { ...this.callInitialData.config, isVKSAudioEnabled: value },
            };
        }
    };

    @action
    public setIsVKSVideoEnabled = (value: boolean) => {
        if (this.callInitialData) {
            this.callInitialData = {
                ...this.callInitialData,
                config: { ...this.callInitialData.config, isVKSVideoEnabled: value },
            };
        }
    };

    @action
    public patchCallData = (patch: Partial<IncomingCallData>) => {
        if (!this.callInitialData) {
            return;
        }
        this.callInitialData = { ...this.callInitialData, ...patch };
    };
}

export const appStore = new AppStore();
